import { useRef, useState } from "react";
import axios from "axios";
import { CheckCircle2, AlertCircle, Loader2, ArrowRight } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL ? `${process.env.REACT_APP_BACKEND_URL}/api` : "";
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
const PHONE_RE = /^[0-9+()\-.\s]{7,20}$/;

const initialValues = (fields) =>
  Object.fromEntries(fields.map((f) => [f.name, f.type === "checkbox" ? false : ""]));

const isVisible = (f, values) => !f.showIf || values[f.showIf.field] === f.showIf.equals;

const validate = (fields, values) => {
  const errs = {};
  fields.filter((f) => isVisible(f, values)).forEach((f) => {
    const v = values[f.name];
    if (f.required && (f.type === "checkbox" ? !v : !String(v).trim())) errs[f.name] = f.requiredMsg || "This field is required.";
    else if (f.type === "email" && v && !EMAIL_RE.test(v)) errs[f.name] = "Enter a valid email address.";
    else if (f.type === "tel" && v && !PHONE_RE.test(v)) errs[f.name] = "Enter a valid phone number.";
    else if (f.minLength && v && String(v).trim().length < f.minLength) errs[f.name] = `Please enter at least ${f.minLength} characters.`;
  });
  return errs;
};

const Field = ({ f, value, error, onChange, id }) => {
  const common = {
    id, name: f.name, "aria-invalid": !!error, "aria-describedby": error ? `${id}-err` : f.hint ? `${id}-hint` : undefined,
    "data-testid": `${id}-input`, required: f.required,
  };
  const label = (
    <span className="mb-2 block text-sm font-semibold text-navy">
      {f.label} {f.required ? <span className="text-fire" aria-hidden="true">*</span> : <span className="font-normal text-slate">(optional)</span>}
    </span>
  );
  let control;
  if (f.type === "select") {
    control = (
      <select {...common} className="field-input appearance-none bg-[url('data:image/svg+xml;utf8,<svg xmlns=%22http://www.w3.org/2000/svg%22 width=%2212%22 height=%228%22><path d=%22M1 1l5 5 5-5%22 stroke=%22%2310263F%22 fill=%22none%22 stroke-width=%221.5%22/></svg>')] bg-[length:12px] bg-[right_1rem_center] bg-no-repeat pr-10" value={value} onChange={(e) => onChange(e.target.value)}>
        <option value="">Select an option</option>
        {f.options.map((o) => <option key={o} value={o}>{o}</option>)}
      </select>
    );
  } else if (f.type === "textarea") {
    control = <textarea {...common} rows={5} maxLength={f.maxLength || 2000} className="field-input resize-y" value={value} onChange={(e) => onChange(e.target.value)} />;
  } else if (f.type === "radio") {
    return (
      <fieldset className={f.half ? "" : "md:col-span-2"} aria-describedby={error ? `${id}-err` : undefined}>
        <legend className="mb-2 block text-sm font-semibold text-navy">{f.label} {f.required && <span className="text-fire" aria-hidden="true">*</span>}</legend>
        <div className="flex gap-3">
          {f.options.map((o) => (
            <label key={o} className={`flex min-h-[48px] cursor-pointer items-center gap-3 rounded-[3px] border px-5 transition-colors ${value === o ? "border-navy bg-navy text-paper" : "border-[#C9D2D9] bg-white text-ink hover:border-navy"}`}>
              <input type="radio" name={f.name} value={o} checked={value === o} onChange={() => onChange(o)} className="sr-only" data-testid={`${id}-${o.toLowerCase()}`} />
              <span className="text-sm font-semibold">{o}</span>
            </label>
          ))}
        </div>
        {error && <p id={`${id}-err`} className="mt-2 text-sm text-fire" role="alert">{error}</p>}
      </fieldset>
    );
  } else if (f.type === "checkbox") {
    return (
      <div className="md:col-span-2">
        <label className="flex cursor-pointer items-start gap-3 text-sm leading-relaxed text-ink">
          <input {...common} type="checkbox" checked={value} onChange={(e) => onChange(e.target.checked)} className="mt-1 h-5 w-5 shrink-0 accent-[#10263F]" />
          <span>{f.label} {f.required && <span className="text-fire" aria-hidden="true">*</span>}</span>
        </label>
        {error && <p id={`${id}-err`} className="mt-2 text-sm text-fire" role="alert">{error}</p>}
      </div>
    );
  } else {
    control = <input {...common} type={f.type || "text"} autoComplete={f.autoComplete} maxLength={f.maxLength || 160} className="field-input" value={value} onChange={(e) => onChange(e.target.value)} />;
  }
  return (
    <label htmlFor={id} className={`block ${f.half ? "" : "md:col-span-2"}`}>
      {label}
      {control}
      {f.hint && !error && <span id={`${id}-hint`} className="mt-2 block text-xs text-slate">{f.hint}</span>}
      {error && <span id={`${id}-err`} className="mt-2 block text-sm text-fire" role="alert">{error}</span>}
    </label>
  );
};

const Success = ({ title, text, reference, onReset, testId }) => (
  <div className="flex flex-col items-start gap-5 border border-gold/40 bg-white p-8 md:p-12" role="status" data-testid={`${testId}-success`}>
    <CheckCircle2 className="text-gold" size={40} />
    <h3 className="text-2xl font-bold">{title}</h3>
    <p className="max-w-xl leading-relaxed text-ink/80">{text}</p>
    <p className="text-sm text-slate">Reference number: <span className="font-semibold text-navy" data-testid={`${testId}-reference`}>{reference}</span></p>
    <button type="button" onClick={onReset} className="btn btn-ghost" data-testid={`${testId}-reset-button`}>Submit another response</button>
  </div>
);

export const FormBuilder = ({ formType, fields, submitLabel, successTitle, successText, footnote, testId }) => {
  const [values, setValues] = useState(() => initialValues(fields));
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState("idle");
  const [message, setMessage] = useState("");
  const [reference, setReference] = useState("");
  const [hp, setHp] = useState("");
  const startedAt = useRef(Date.now());

  const reset = () => {
    setValues(initialValues(fields));
    setErrors({});
    setStatus("idle");
    startedAt.current = Date.now();
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    const errs = validate(fields, values);
    setErrors(errs);
    if (Object.keys(errs).length) {
      setStatus("error");
      setMessage("Please review the highlighted fields.");
      document.getElementById(`${testId}-${Object.keys(errs)[0]}`)?.focus();
      return;
    }
    if (!API) {
      setStatus("error");
      setMessage("Online submissions are temporarily unavailable. Please try again later.");
      return;
    }
    setStatus("loading");
    const payload = Object.fromEntries(
      fields.filter((f) => isVisible(f, values)).map((f) => [f.name, values[f.name] === "" ? null : values[f.name]])
    );
    try {
      const res = await axios.post(`${API}/forms/${formType}`, { ...payload, hp, elapsed_ms: Date.now() - startedAt.current });
      setReference(res.data?.reference ?? "");
      setStatus("success");
    } catch (err) {
      const detail = err.response?.data?.detail;
      setErrors(detail?.fields ?? {});
      setMessage(detail?.message ?? "We could not send your submission. Please check your connection and try again.");
      setStatus("error");
    }
  };

  if (status === "success") return <Success title={successTitle} text={successText} reference={reference} onReset={reset} testId={testId} />;

  return (
    <form onSubmit={onSubmit} noValidate className="relative grid gap-6 md:grid-cols-2" data-testid={testId}>
      <div className="absolute -left-[9999px]" aria-hidden="true">
        <label>Leave this field empty<input tabIndex={-1} autoComplete="off" value={hp} onChange={(e) => setHp(e.target.value)} name="website" /></label>
      </div>
      {fields.filter((f) => isVisible(f, values)).map((f) => (
        <Field key={f.name} f={f} id={`${testId}-${f.name}`} value={values[f.name]} error={errors[f.name]} onChange={(v) => setValues((s) => ({ ...s, [f.name]: v }))} />
      ))}
      {status === "error" && (
        <p className="flex items-start gap-3 border-l-2 border-fire bg-[rgba(183,53,53,0.06)] p-4 text-sm text-fire md:col-span-2" role="alert" data-testid={`${testId}-error`}>
          <AlertCircle size={18} className="mt-0.5 shrink-0" /> {message}
        </p>
      )}
      <div className="flex flex-col gap-4 md:col-span-2 md:flex-row md:items-center md:justify-between">
        {footnote && <p className="max-w-lg text-xs leading-relaxed text-slate">{footnote}</p>}
        <button type="submit" disabled={status === "loading"} className="btn btn-navy disabled:opacity-60" data-testid={`${testId}-submit-button`}>
          {status === "loading" ? <><Loader2 size={16} className="animate-spin" /> Sending</> : <>{submitLabel} <ArrowRight size={16} /></>}
        </button>
      </div>
    </form>
  );
};
